import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";

const FORM_URL = "https://readdy.ai/api/form/db7jgcomkl3spn8sm9c0";

const highlights = [
  {
    icon: "ri-information-line",
    title: "资料整理",
    desc: "系统梳理大岛优香的基本信息与演艺经历，方便快速查阅。",
  },
  {
    icon: "ri-film-line",
    title: "作品归档",
    desc: "按类型与年份收录影视作品，提供剧情简介与角色介绍。",
  },
  {
    icon: "ri-newspaper-line",
    title: "资讯聚合",
    desc: "持续更新最新动态、娱乐新闻与活动预告，紧跟她的每一步。",
  },
  {
    icon: "ri-chat-heart-line",
    title: "粉丝连接",
    desc: "提供话题讨论与留言空间，让喜欢她的人能够彼此交流。",
  },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于本站 - 大岛优香明星资讯站",
    description:
      "了解大岛优香明星资讯站的建设初衷、内容范围与联系方式，本站为粉丝自建的非官方资讯聚合平台。",
    keywords: "关于本站,大岛优香资讯站,联系方式,版权声明",
  });

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("company_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      setFeedback("感谢你的反馈，我们已经收到，会尽快查看。");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      const str = String(value).trim();
      if (str) params.append(key, str);
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.detail || parsed?.meta?.message || parsed?.message || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("感谢你的反馈，我们已经收到，会尽快查看。");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(
          isSpam ? "提交未通过校验，请检查内容后重试。" : `提交失败：${serverMsg || "请稍后再试"}`
        );
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
        <Link to="/" className="hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
        <span className="text-foreground-800">关于本站</span>
      </nav>

      <div className="max-w-3xl mb-10">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
          关于本站
        </h1>
        <p className="text-[15px] text-foreground-700 leading-8">
          本站以「大岛优香」为核心主题，是一个由粉丝自建的非官方资讯聚合平台。
          我们希望把分散在各处的资料、作品与动态整理到一处，让喜欢她的人可以更方便地了解她、
          走近她，也为同好们提供一个温和、友好的交流空间。
        </p>
      </div>

      {/* Highlights */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
        {highlights.map((h) => (
          <div key={h.title} className="bg-background-100 border border-background-200 rounded-lg p-5">
            <span className="w-11 h-11 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-4">
              <i className={`${h.icon} text-xl`} />
            </span>
            <h3 className="font-heading font-bold text-foreground-950 mb-1.5">{h.title}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{h.desc}</p>
          </div>
        ))}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
        {/* Feedback form */}
        <div className="lg:col-span-2">
          <div className="bg-background-100 border border-background-200 rounded-lg p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-2">
              反馈与建议
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              对本站内容有任何建议、纠错或合作意向，欢迎通过下面的表单告诉我们。
            </p>
            <form data-readdy-form id="about-feedback-form" onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">
                    你的称呼
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="怎么称呼你"
                    className="w-full bg-background-50 border border-background-200 rounded-md px-3 py-2 text-sm outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于我们回复你"
                    className="w-full bg-background-50 border border-background-200 rounded-md px-3 py-2 text-sm outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">
                  反馈内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="说说你的想法（500 字以内）"
                  className="w-full bg-background-50 border border-background-200 rounded-md px-3 py-2 text-sm outline-none focus:border-primary-400 transition-colors resize-none"
                />
              </div>

              {/* anti-spam honeypot */}
              <input
                type="text"
                name="company_alt"
                className="hp-field-guard"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                提交反馈
              </button>

              {status !== "idle" && (
                <p
                  className={`text-sm rounded-md px-3 py-2 animate-fade-up ${
                    status === "success"
                      ? "bg-primary-100 text-primary-800"
                      : "bg-accent-100 text-accent-900"
                  }`}
                >
                  {feedback}
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Contact + disclaimer */}
        <aside className="space-y-6">
          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-5">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">联系方式</h2>
            <ul className="space-y-3 text-sm text-foreground-700">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-600 w-4 h-4 flex items-center justify-center" />
                contact@oshima-yuka.example
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-map-pin-line text-primary-600 w-4 h-4 flex items-center justify-center" />
                内容合作 · 线上沟通
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-time-line text-primary-600 w-4 h-4 flex items-center justify-center" />
                工作日 10:00 - 18:00 回复
              </li>
            </ul>
          </div>

          <div className="bg-accent-50 border border-accent-200 rounded-lg p-5">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">
              版权与免责
            </h2>
            <p className="text-sm text-foreground-700 leading-relaxed mb-3">
              本站为非官方粉丝资讯站，站内所有资料、作品与动态均为演示示例，仅供学习交流使用，
              不代表明星本人或其经纪公司的官方立场。
            </p>
            <p className="text-sm text-foreground-700 leading-relaxed">
              如涉及内容更正或侵权，请通过上方邮箱与我们联系，我们会及时处理。
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}